import React from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";

import { Run, Team } from "../types";
import { RunStatusChip } from "../components/StatusChip";
import StagePipeline from "../components/StagePipeline";
import { formatDateTime, formatDuration } from "../utils/format";

interface RunsProps {
  team: Team;
  runs: Run[];
  onTriggerRun: () => void;
}

const Runs: React.FC<RunsProps> = ({ team, runs, onTriggerRun }) => {
  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2.5 }}
      >
        <Box>
          <Typography variant="h5">Runs</Typography>
          <Typography variant="body2" color="text.secondary">
            Run history for {team.name}. Each run executes the canonical stage
            pipeline.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<PlayArrowRoundedIcon />}
          onClick={onTriggerRun}
        >
          Trigger run
        </Button>
      </Stack>

      <Stack spacing={2}>
        {runs.map((run) => (
          <Card key={run.id}>
            <CardContent>
              <Stack
                direction={{ xs: "column", md: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", md: "center" }}
                spacing={2}
              >
                <Box sx={{ minWidth: 240 }}>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ mb: 0.5 }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{ fontFamily: "monospace" }}
                    >
                      {run.id}
                    </Typography>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={run.trigger}
                      sx={{ textTransform: "capitalize" }}
                    />
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {formatDateTime(run.startedAt)} ·{" "}
                    {formatDuration(run.startedAt, run.finishedAt)}
                  </Typography>
                </Box>

                <Box sx={{ flex: 1, width: "100%", px: { md: 2 } }}>
                  <StagePipeline stages={run.stages} />
                </Box>

                <RunStatusChip status={run.status} size="medium" />
              </Stack>
            </CardContent>
          </Card>
        ))}
        {runs.length === 0 && (
          <Card>
            <CardContent>
              <Typography color="text.secondary">
                No runs recorded for this team yet.
              </Typography>
            </CardContent>
          </Card>
        )}
      </Stack>
    </Box>
  );
};

export default Runs;
